import { GHOST_HZ, GHOST_MAX_FRAMES } from '../shared/constants';
import type { GhostFrame } from '../shared/types';
import type { Sim } from '../shared/sim';

/** Records positions (not inputs) so playback can never desync from physics. */
export class GhostRecorder {
  readonly frames: GhostFrame[] = [];
  private nextAt = 0;
  private readonly stepMs = 1000 / GHOST_HZ;

  sample(sim: Sim): void {
    if (sim.timeMs < this.nextAt) return;
    if (this.frames.length >= GHOST_MAX_FRAMES) return;
    this.nextAt = sim.timeMs + this.stepMs;
    this.frames.push({
      t: Math.round(sim.timeMs),
      z: Math.round(sim.z),
      x: Math.round(sim.x * 1000) / 1000,
      y: Math.round(sim.y),
      s: Math.round(sim.speed),
    });
  }

  reset(): void {
    this.frames.length = 0;
    this.nextAt = 0;
  }
}

/** Interpolating playback of a recorded run. */
export class GhostPlayer {
  private i = 0;

  constructor(readonly frames: GhostFrame[]) {}

  get valid(): boolean {
    return this.frames.length > 1;
  }

  get durationMs(): number {
    return this.frames.length ? this.frames[this.frames.length - 1]!.t : 0;
  }

  reset(): void {
    this.i = 0;
  }

  at(ms: number): GhostFrame | null {
    const f = this.frames;
    if (f.length === 0) return null;
    if (ms <= f[0]!.t) return f[0]!;
    const last = f[f.length - 1]!;
    if (ms >= last.t) return last;
    // Playback is monotonic, so walk forward from where we were.
    if (f[this.i]!.t > ms) this.i = 0;
    while (this.i < f.length - 2 && f[this.i + 1]!.t <= ms) this.i++;
    const a = f[this.i]!;
    const b = f[this.i + 1]!;
    const span = Math.max(1, b.t - a.t);
    const k = (ms - a.t) / span;
    return {
      t: ms,
      z: a.z + (b.z - a.z) * k,
      x: a.x + (b.x - a.x) * k,
      y: a.y + (b.y - a.y) * k,
      s: a.s + (b.s - a.s) * k,
    };
  }
}
